/**
 * OpenAI 兼容生图适配器（T11a）。
 *
 * 覆盖所有暴露 `POST /v1/images/generations` 的服务：OpenAI 官方、
 * Azure OpenAI 兼容网关、自建 one-api / new-api 网关等。**不使用任何 SDK。**
 *
 * 关键行为：
 * - 请求体固定为 `model` / `prompt` / `n` / `size` / `response_format`；
 * - 返回的 `b64_json` 与 `url` 两种形态统一转成 `dataUrl`（URL 会下载后内联，
 *   这样「存图」不依赖远端链接长期有效）；
 * - 超时 / 重试 / 取消 / 错误规范化复用 `../providers/http`；
 * - 生成请求与「URL 内联下载」两条路径都做同样的取消 / 错误归一：
 *   取消 -> `ImageAbortError`（`name === 'AbortError'`），其余 -> `ImageError`；
 * - 畸形响应结构一律归一为 `ImageError{code:'parse'}`，不泄漏 `TypeError`。
 */

import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  isAbortError,
  jsonHeaders,
  normalizeBaseUrl,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  resolveDeps,
  toProviderError,
  type ResolvedDeps,
} from '../providers/http';
import type { FetchLike } from '../providers/types';
import {
  ImageAbortError,
  ImageError,
  type GeneratedImage,
  type ImageAdapter,
  type ImageDeps,
  type ImageErrorCode,
  type ImageRequest,
  type ResolvedImageConfig,
} from './types';

export const OPENAI_IMAGE_KIND = 'openai-compatible' as const;

/** 默认生成张数。 */
export const DEFAULT_IMAGE_COUNT = 1;
/** 单次请求允许的最大生成张数（超出会被钳制）。 */
export const MAX_IMAGE_COUNT = 10;

/** `/v1/images/generations` 完整地址（复用 providers/http 的 baseUrl 规范化）。 */
export function imageGenerationsUrl(baseUrl: string): string {
  const url = new URL(normalizeBaseUrl(baseUrl));
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/images/generations`;
  return url.toString();
}

/** 张数钳制：非法 / 非正数回落到默认值，上限 10。 */
export function clampImageCount(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return DEFAULT_IMAGE_COUNT;
  const floored = Math.floor(value);
  if (floored < 1) return DEFAULT_IMAGE_COUNT;
  return Math.min(floored, MAX_IMAGE_COUNT);
}

export interface OpenAIImageRequestBody {
  model: string;
  prompt: string;
  n: number;
  response_format: 'b64_json';
  size?: string;
}

/** 组装请求体；字段名与 OpenAI 生图接口一致。 */
export function buildImageBody(req: ImageRequest, model: string): OpenAIImageRequestBody {
  const body: OpenAIImageRequestBody = {
    model,
    prompt: req.prompt,
    n: clampImageCount(req.n),
    response_format: 'b64_json',
  };
  if (req.size) body.size = req.size;
  return body;
}

/* ------------------------------------------------------------------ *
 * MIME 识别
 * ------------------------------------------------------------------ */

/** 魔数嗅探：识别常见图片格式，识别不出返回 undefined。 */
export function sniffMimeFromBytes(bytes: Uint8Array): string | undefined {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return 'image/gif';
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return undefined;
}

/** 从 base64 头几个字节识别常见图片 MIME，识别不出时回落到 image/png。 */
export function detectMimeFromBase64(b64: string): string {
  let head: string;
  try {
    head = atob(b64.slice(0, 64));
  } catch {
    return 'image/png';
  }
  const bytes = Uint8Array.from(head, (char) => char.charCodeAt(0) & 0xff);
  return sniffMimeFromBytes(bytes) ?? 'image/png';
}

/** 从 URL（含 data URL 与文件后缀）推断 MIME。 */
export function detectMimeFromUrl(url: string): string {
  const dataUrlMatch = /^data:([^;,]+)[;,]/.exec(url);
  if (dataUrlMatch?.[1]) return dataUrlMatch[1];
  const withoutQuery = url.split(/[?#]/)[0] ?? url;
  const ext = withoutQuery.slice(withoutQuery.lastIndexOf('.') + 1).toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  return 'image/png';
}

/** 取响应头里的 `image/*` MIME；缺失或非图片时返回 undefined。 */
export function imageMimeFromContentType(contentType: string | null): string | undefined {
  if (!contentType) return undefined;
  const mime = contentType.split(';')[0]?.trim().toLowerCase();
  return mime?.startsWith('image/') ? mime : undefined;
}

/**
 * 图片 GET 专用请求头。
 *
 * 不能用 `jsonHeaders`：那会带上 `Accept: application/json` 与
 * `Content-Type: application/json`，部分图床会因此直接拒绝。
 */
export function imageRequestHeaders(
  apiKey?: string,
  extra?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'image/*', ...extra };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
  return headers;
}

/** Uint8Array -> base64（分块，避免超长参数导致调用栈溢出）。 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

/** 把服务端返回的 `url` 解析成可下载的绝对地址。 */
function resolveImageUrl(rawUrl: string, baseUrl: string): string {
  if (/^data:/i.test(rawUrl)) return rawUrl;
  try {
    return new URL(rawUrl, normalizeBaseUrl(baseUrl)).toString();
  } catch {
    throw new ImageError(`生图返回的图片 URL 非法: ${rawUrl}`, 'parse');
  }
}

/** ProviderErrorCode -> ImageErrorCode（两套枚举取值一致，此处显式映射避免断言）。 */
function toImageErrorCode(code: string): ImageErrorCode {
  switch (code) {
    case 'http':
    case 'timeout':
    case 'network':
    case 'aborted':
    case 'parse':
    case 'config':
    case 'unsupported':
      return code;
    default:
      return 'unknown';
  }
}

/** 把任意抛出物规范化成 ImageError / ImageAbortError。 */
export function toImageError(error: unknown): ImageError {
  if (error instanceof ImageError) return error;
  if (isAbortError(error)) return new ImageAbortError();
  if (error instanceof ProviderHttpError) {
    return new ImageError(error.message, toImageErrorCode(error.code), error.status);
  }
  return new ImageError(toProviderError(error).message, 'network');
}

/* ------------------------------------------------------------------ *
 * 返回体解析
 * ------------------------------------------------------------------ */

interface OpenAIImageItem {
  b64_json?: unknown;
  url?: unknown;
  revised_prompt?: unknown;
}

interface OpenAIImageResponse {
  data?: unknown;
  error?: unknown;
}

function errorMessageOf(rawError: unknown): string {
  if (rawError && typeof rawError === 'object') {
    const message = (rawError as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return '生图服务返回错误';
}

/** 从服务端返回里取出图片条目；结构非法一律抛 `ImageError{code:'parse'}`。 */
export function parseImageItems(payload: unknown): OpenAIImageItem[] {
  if (!payload || typeof payload !== 'object') {
    throw new ImageError('生图响应不是合法对象', 'parse');
  }
  const json = payload as OpenAIImageResponse;

  if (json.error) {
    throw new ImageError(errorMessageOf(json.error), 'http');
  }

  const data = json.data;
  if (!Array.isArray(data)) {
    throw new ImageError('生图响应缺少 data 数组', 'parse');
  }
  if (data.length === 0) {
    throw new ImageError('生图服务没有返回任何图片', 'parse');
  }

  return data.map((entry) => {
    if (!entry || typeof entry !== 'object') {
      throw new ImageError('生图返回的条目不是对象', 'parse');
    }
    return entry as OpenAIImageItem;
  });
}

/* ------------------------------------------------------------------ *
 * 适配器
 * ------------------------------------------------------------------ */

export class OpenAIImageAdapter implements ImageAdapter {
  readonly id: string;
  readonly kind = OPENAI_IMAGE_KIND;
  private readonly config: ResolvedImageConfig;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly retryDelayMs: number;
  private readonly extraHeaders: Record<string, string>;

  constructor(config: ResolvedImageConfig, deps?: ImageDeps) {
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    this.fetchImpl = resolved.fetch;
    this.timeoutMs = clampTimeoutMs(config.timeoutMs, resolved.timeoutMs);
    this.retries = clampRetries(config.retries, resolved.retries);
    this.retryDelayMs = clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs);
    // 自定义头：ImageConfig.headers 覆盖 ImageDeps.extraHeaders。
    this.extraHeaders = { ...resolved.extraHeaders, ...(config.headers ?? {}) };
  }

  private deps(): ResolvedDeps {
    return {
      fetch: this.fetchImpl,
      timeoutMs: this.timeoutMs,
      retries: this.retries,
      retryDelayMs: this.retryDelayMs,
      extraHeaders: this.extraHeaders,
    };
  }

  /**
   * 下载 URL 形态的结果并内联成 data URL。
   *
   * 与生成请求走同一套契约：取消 -> ImageAbortError，其余失败 -> ImageError
   * （保留 code / status），MIME 优先用响应 Content-Type，其次嗅探实测字节，
   * 最后才回落到 URL 后缀。
   */
  private async downloadAsDataUrl(
    rawUrl: string,
    signal?: AbortSignal,
  ): Promise<{ dataUrl: string; mime: string }> {
    const url = resolveImageUrl(rawUrl, this.config.baseUrl);
    if (/^data:/i.test(url)) {
      return { dataUrl: url, mime: detectMimeFromUrl(url) };
    }
    if (signal?.aborted) throw new ImageAbortError();

    // 只有同源（同一网关）才携带鉴权头，避免把 API Key 泄漏给第三方图床。
    const sameOrigin = new URL(url).origin === new URL(normalizeBaseUrl(this.config.baseUrl)).origin;
    let opened;
    try {
      opened = await openResponseWithRetry({
        url,
        init: {
          method: 'GET',
          headers: imageRequestHeaders(
            sameOrigin ? this.config.apiKey : undefined,
            sameOrigin ? this.extraHeaders : undefined,
          ),
        },
        deps: this.deps(),
        externalSignal: signal,
      });
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) throw new ImageAbortError();
      throw toImageError(error);
    }

    const { response, guard } = opened;
    try {
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength === 0) {
        throw new ImageError('生图返回的图片内容为空', 'parse');
      }
      const bytes = new Uint8Array(buffer);
      const mime =
        imageMimeFromContentType(response.headers.get('content-type')) ??
        sniffMimeFromBytes(bytes) ??
        detectMimeFromUrl(url);
      return { dataUrl: `data:${mime};base64,${bytesToBase64(bytes)}`, mime };
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) throw new ImageAbortError();
      throw toImageError(error);
    } finally {
      guard.dispose();
    }
  }

  async generate(req: ImageRequest): Promise<GeneratedImage[]> {
    // 契约：取消时以 AbortError 结束，调用方据此区分取消与失败。
    if (req.signal?.aborted) throw new ImageAbortError();

    const model = this.config.defaultModel?.trim();
    if (!model) {
      throw new ImageError('生图配置缺少 defaultModel', 'config');
    }

    let opened;
    try {
      opened = await openResponseWithRetry({
        url: imageGenerationsUrl(this.config.baseUrl),
        init: {
          method: 'POST',
          headers: jsonHeaders(this.config.apiKey, this.extraHeaders),
          body: JSON.stringify(buildImageBody(req, model)),
        },
        deps: this.deps(),
        externalSignal: req.signal,
      });
    } catch (error) {
      if (req.signal?.aborted || isAbortError(error)) throw new ImageAbortError();
      throw toImageError(error);
    }

    const { response, guard } = opened;
    let payload: unknown;
    try {
      payload = await readJsonBody<unknown>(response);
    } catch (error) {
      if (req.signal?.aborted || isAbortError(error)) throw new ImageAbortError();
      throw toImageError(error);
    } finally {
      guard.dispose();
    }

    if (req.signal?.aborted) throw new ImageAbortError();

    const images: GeneratedImage[] = [];
    let items: OpenAIImageItem[];
    try {
      items = parseImageItems(payload);
    } catch (error) {
      if (req.signal?.aborted || isAbortError(error)) throw new ImageAbortError();
      throw toImageError(error);
    }

    for (const item of items) {
      try {
        const revisedPrompt =
          typeof item.revised_prompt === 'string' ? item.revised_prompt : undefined;

        if (typeof item.b64_json === 'string' && item.b64_json.length > 0) {
          const mime = detectMimeFromBase64(item.b64_json);
          images.push({
            dataUrl: `data:${mime};base64,${item.b64_json}`,
            mime,
            ...(revisedPrompt ? { revisedPrompt } : {}),
          });
          continue;
        }

        if (typeof item.url === 'string' && item.url.length > 0) {
          const downloaded = await this.downloadAsDataUrl(item.url, req.signal);
          images.push({
            dataUrl: downloaded.dataUrl,
            mime: downloaded.mime,
            ...(revisedPrompt ? { revisedPrompt } : {}),
          });
          continue;
        }

        throw new ImageError('生图返回的条目既没有 b64_json 也没有 url', 'parse');
      } catch (error) {
        if (req.signal?.aborted || isAbortError(error)) throw new ImageAbortError();
        throw toImageError(error);
      }
    }

    return images;
  }
}

export function createOpenAIImageAdapter(
  config: ResolvedImageConfig,
  deps?: ImageDeps,
): ImageAdapter {
  return new OpenAIImageAdapter(config, deps);
}